import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { cn, prefersReducedMotion } from '../../lib/utils';

export type ToolStatus = 'running' | 'done' | 'error';

export interface ToolBlockProps {
  /** Tool name, e.g., "read_file", "search_knowledge". */
  tool: string;
  /** Short description / args, e.g., "page 4 of Q3.pdf". */
  detail?: ReactNode;
  status: ToolStatus;
  /** Optional duration, e.g., "1.4s". */
  duration?: string;
  className?: string;
}

/**
 * Inline tool-use reveal. Used to show AI's tool calls in real time during streaming.
 * Slides in from left, status icon updates when complete.
 */
export function ToolBlock({ tool, detail, status, duration, className }: ToolBlockProps) {
  const reduced = prefersReducedMotion();

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, x: -8 }}
      animate={reduced ? { opacity: 1, x: 0 } : { opacity: 1, x: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'flex items-center gap-2 rounded-md p-2.5',
        'border border-border-subtle bg-bg-sunken',
        'font-mono text-xs text-text-secondary',
        className
      )}
    >
      <div
        className={cn(
          'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-[10px] font-semibold',
          status === 'running' && 'animate-pulse bg-info-100 text-info-600',
          status === 'done' && 'bg-success-100 text-success-600',
          status === 'error' && 'bg-error-100 text-error-600'
        )}
        aria-hidden
      >
        {status === 'done' ? '✓' : status === 'error' ? '!' : '⤓'}
      </div>
      <span className="min-w-0 flex-1 truncate">
        <strong className="font-medium text-text-primary">{tool}</strong>
        {detail && <> · {detail}</>}
      </span>
      {duration && status === 'done' && (
        <span className="text-[11px] text-success-600">✓ done in {duration}</span>
      )}
      {status === 'error' && <span className="text-[11px] text-error-600">failed</span>}
    </motion.div>
  );
}
